import { createSimpleContext } from "@mux/ui/context"
import { useSessionParams } from "@/hooks/use-session-params"
import { batch, createEffect, createMemo, createSignal, onCleanup } from "solid-js"
import { createStore, reconcile } from "solid-js/store"
import { useModels } from "@/context/models"
import { useProviders } from "@/hooks/use-providers"
import { modelEnabled, modelProbe } from "@/testing/model-selection"
import { DevicePreference, persisted } from "@/storage/persist"
import {
  cycleModelVariant,
  getConfiguredAgentVariant,
  listModelVariants,
  messageCanRestoreModel,
  resolveModelVariant,
} from "./model-variant"
import {
  cloneSelection,
  draftSeed as seedFrom,
  messageSelection,
  migrateSavedSelections,
  modelKey,
  sameModel,
  selectedVariant,
  sessionSelection,
  variantForModelPick,
  withAccountVariants,
  wrapIndex,
  type ModelKey,
  type SavedSelections,
  type SelectionState as State,
} from "./local-selection"
import { useGlobalSync } from "./global-sync"
import { pinnedSessionAgent } from "./mux-oracle/identity"
import { sessionModelHydration } from "./session-model-hydration"
import { resolveExistingSessionModelState, sameSessionModelState } from "./session-model-state"

export type { ModelKey } from "./local-selection"

export const { use: useGlobalLocal, provider: GlobalLocalProvider } = createSimpleContext({
  name: "GlobalLocal",
  init: () => {
    const [store, setStore, _, ready] = persisted(
      {
        ...DevicePreference.global("model-selection"),
        migrate: migrateSavedSelections,
      },
      createStore<SavedSelections>({
        session: {},
        drafts: {},
        draft: undefined,
        lastUsed: undefined,
      }),
    )
    return { store, setStore, ready }
  }
})

export const { use: useLocal, provider: LocalProvider } = createSimpleContext({
  name: "Local",
  init: () => {
    const params = useSessionParams()
    const globalSync = useGlobalSync()
    const globalLocal = useGlobalLocal()
    const providers = useProviders()
    const models = useModels()

    const saved = globalLocal.store
    const setSaved = globalLocal.setStore
    const savedReady = globalLocal.ready
    const userSelectedSessions = new Set<string>()
    const [submittedSession, setSubmittedSession] = createSignal<string>()

    const draftSeed = () => seedFrom(saved.lastUsed)
    const draftState = (stored: State | undefined) =>
      withAccountVariants(stored ?? draftSeed(), models.variantDefaults())

    const routeID = createMemo(() => params.id || undefined)
    const draftID = createMemo(() => {
      const route = routeID()
      return route?.startsWith("unsaved:") ? route : undefined
    })
    const id = createMemo(() => {
      const session = routeID()
      return session?.startsWith("unsaved:") ? undefined : session
    })

    const activeDir = createMemo(() => {
      const activeSessionID = id()
      if (!activeSessionID) return params.dir || ""
      const session = globalSync.data.session.find((s) => s.id === activeSessionID)
      return session?.workspaceID || session?.directory || params.dir || ""
    })

    const sessionStore = createMemo(() => {
      const dir = activeDir()
      if (!dir) return undefined
      return globalSync.peekChild(dir)?.[0]
    })

    const list = createMemo(() => {
      const store = sessionStore()
      if (!store) return []
      if (!Array.isArray(store.agent)) return []
      if (params.oracle) {
        return store.agent.filter((item) => item.mode !== "subagent" && item.name === "oracle")
      }
      const activeSessionID = id()
      const sessionAgent = activeSessionID
        ? globalSync.session.get(activeSessionID)?.agent
        : undefined
      return store.agent.filter((item) => item.mode !== "subagent" && (!item.hidden || item.name === sessionAgent))
    })

    const newChatAgent = (name: string | undefined) => {
      const items = sessionStore()?.agent?.filter((item) => item.mode !== "subagent" && !item.hidden) ?? []
      return items.find((item) => item.name === name)?.name
        ?? items.find((item) => item.name === saved.lastUsed?.agent)?.name
        ?? items[0]?.name
    }

    const connected = createMemo(() => new Set(providers.connected().map((item) => item.id)))

    const [store, setStore] = createStore<{
      current?: string
    }>({})

    const validModel = (model: ModelKey) => {
      const provider = providers.all().find((item) => item.id === model.providerID)
      const entry = provider?.models[model.modelID]
      return !!entry && !entry.unlisted && connected().has(model.providerID)
    }

    const firstModel = (...items: Array<() => ModelKey | undefined>) => {
      for (const item of items) {
        const model = item()
        if (!model) continue
        if (validModel(model)) return model
      }
    }

    const pickAgent = (name: string | undefined) => {
      const items = list()
      if (items.length === 0) return undefined
      return items.find((item) => item.name === name) ?? items[0]
    }

    createEffect(() => {
      const items = list()
      if (items.length === 0) {
        if (store.current !== undefined) setStore("current", undefined)
        return
      }
      if (items.some((item) => item.name === store.current)) return
      setStore("current", items[0]?.name)
    })

    const scope = createMemo<State | undefined>(() => {
      const session = id()
      if (session) {
        const cached = saved.session[session]
        const info = globalSync.session.get(session)
        const server = info ? sessionSelection(info, cached) : undefined
        return resolveExistingSessionModelState({
          server,
          cached,
          provisional: globalSync.session.isProvisional(session),
        })
      }
      const draft = draftID()
      return draftState(draft ? saved.drafts?.[draft] : saved.draft)
    })

    const persistSessionSelection = (next: { agent?: string; model?: ModelKey; variant?: string | null }) => {
      const session = id()
      const model = next.model
      const directory = activeDir()
      if (!session || !directory || !model) return

      const restoreConfirmedSelection = () => {
        userSelectedSessions.delete(session)
        const confirmed = globalSync.session.get(session)
        if (confirmed) result.session.restoreFromSession(confirmed)
      }

      const info = globalSync.session.get(session)
      void globalSync.session
        .updateModel(directory, session, {
          agent: pinnedSessionAgent(info) ?? (next.agent || "build"),
          providerID: model.providerID,
          modelID: model.modelID,
          variant: next.variant ?? "",
        })
        .then(restoreConfirmedSelection)
        .catch((error) => {
          restoreConfirmedSelection()
          console.error("Failed to persist session model selection", error)
        })
    }

    const configuredModel = () => {
      const store = sessionStore()
      if (!store || !store.config.model) return
      const [providerID, modelID] = store.config.model.split("/")
      const model = { providerID, modelID }
      if (validModel(model)) return model
    }

    const recentModel = () => {
      for (const item of models.recent.list()) {
        if (validModel(item)) return item
      }
    }

    const defaultModel = () => {
      const defaults = providers.default()
      for (const provider of providers.connected()) {
        const configured = defaults[provider.id]
        if (configured) {
          const model = { providerID: provider.id, modelID: configured }
          if (validModel(model)) return model
        }

        const first = Object.values(provider.models).find((model) => !model.unlisted)
        if (!first) continue
        const model = { providerID: provider.id, modelID: first.id }
        if (validModel(model)) return model
      }
    }

    const fallback = createMemo<ModelKey | undefined>(() => configuredModel() ?? recentModel() ?? defaultModel())

    const agent = {
      list,
      selection() {
        return id() ? (scope()?.agent ?? agent.current()?.name) : agent.current()?.name
      },
      current() {
        return pickAgent(scope()?.agent ?? store.current)
      },
      set(name: string | undefined) {
        const item = pickAgent(name)
        if (!item) {
          setStore("current", undefined)
          return
        }
        if (agent.current()?.name === item.name) return

        const next = {
          ...snapshot(),
          agent: item.name,
          source: "user",
          messageID: scope()?.messageID,
        } satisfies State
        batch(() => {
          if (id()) userSelectedSessions.add(id()!)
          setStore("current", item.name)
          write(next)
          persistSessionSelection(next)
        })
      },
      move(direction: 1 | -1) {
        const items = list()
        if (items.length === 0) {
          setStore("current", undefined)
          return
        }

        const index = items.findIndex((item) => item.name === agent.current()?.name)
        const item = items[wrapIndex(index, direction, items.length)]
        if (!item) return
        agent.set(item.name)
      },
    }

    const current = () => {
      if (!savedReady()) return undefined

      // Session-scoped model wins. Never fall back to global recent while a chat
      // already has an explicit model (even if temporarily unavailable).
      const scoped = scope()?.model
      if (scoped) {
        const found = models.find(scoped)
        if (found && (id() || validModel(scoped))) return found
        if (id()) return undefined
      }

      const item = firstModel(
        () => saved.lastUsed?.model,
        () => agent.current()?.model,
        fallback,
      )
      if (!item) return undefined
      return models.find(item)
    }

    const selectedModel = () => {
      const scoped = scope()?.model
      if (id() && scoped) return scoped
      if (!savedReady()) return undefined
      const item = current()
      if (!item) return undefined
      return { providerID: item.provider.id, modelID: item.id }
    }

    const configured = () => {
      const item = agent.current()
      const model = current()
      if (!item || !model) return undefined
      return getConfiguredAgentVariant({
        agent: { model: item.model, variant: item.variant },
        model: { providerID: model.provider.id, modelID: model.id, variants: model.variants },
      })
    }

    const selected = () => selectedVariant({ state: scope(), active: selectedModel(), session: !!id() })

    const snapshot = () => {
      const key = selectedModel()
      const selectedVariant = selected()
      const variant = selectedVariant ?? null
      return {
        agent: agent.selection(),
        model: key,
        variant,
        variants: scope()?.variants ? { ...scope()!.variants } : undefined,
      } satisfies State
    }

    // The draft slot for the current route: a named unsaved chat, else the workspace draft.
    const setDraft = (value: State | undefined, merge: boolean) => {
      const draft = draftID()
      if (draft) {
        if (!saved.drafts) setSaved("drafts", {})
        if (merge) setSaved("drafts", draft, reconcile(value ?? {}))
        else setSaved("drafts", draft, value)
        return
      }
      if (merge) setSaved("draft", reconcile(value ?? {}))
      else setSaved("draft", value)
    }

    const write = (next: Partial<State>) => {
      const state = {
        ...(scope() ?? { agent: agent.current()?.name }),
        ...next,
      } satisfies State

      const session = id()
      if (session) {
        setSaved("session", session, reconcile(state))
        return
      }
      setDraft(state, true)
    }

    const recent = createMemo(() =>
      models.recent
        .list()
        .map((entry) => {
          const found = models.find(entry)
          if (!found || found.unlisted) return undefined
          return { model: found, variant: entry.variant ?? null }
        })
        .filter((entry) => entry !== undefined),
    )

    // Cycling walks distinct models; thinking levels only matter inside the picker.
    const cycleTargets = createMemo(() => {
      const seen = new Set<string>()
      return recent().filter((entry) => {
        const key = modelKey({ providerID: entry.model.provider.id, modelID: entry.model.id })
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    })

    const model = {
      ready: () => savedReady() && models.known(),
      selection: selectedModel,
      current,
      recent,
      list: models.list,
      selectable: models.selectable,
      providerLabel: models.providerLabel,
      providerName: (provider: Parameters<typeof models.providerName>[0]) => models.providerName(provider).name,
      cycle(direction: 1 | -1) {
        const items = cycleTargets()
        const item = current()
        if (!item) return

        const index = items.findIndex(
          (entry) => entry.model.provider.id === item.provider.id && entry.model.id === item.id,
        )
        if (index === -1) return

        const entry = items[wrapIndex(index, direction, items.length)]
        if (!entry) return
        model.set({ providerID: entry.model.provider.id, modelID: entry.model.id })
      },
      set(item: ModelKey | undefined, options?: { variant?: string | null }) {
        batch(() => {
          if (id()) userSelectedSessions.add(id()!)
          // Restore this chat's per-model variant. Global preference is only a
          // default for drafts, never for an existing session.
          const current = scope()
          const variants = current?.variants
          const key = item ? modelKey(item) : undefined
          const explicit = !!options && "variant" in options
          const nextVariant = variantForModelPick({
            state: current,
            model: item,
            session: !!id(),
            explicit: explicit ? options : undefined,
          })

          if (current && sameModel(current.model, item) && current.variant === nextVariant) return
          write({
            model: item,
            variant: nextVariant,
            variants: key && explicit ? { ...(variants ?? {}), [key]: nextVariant } : variants,
            source: "user",
            messageID: scope()?.messageID,
          })
          if (!item) return
          persistSessionSelection({ agent: agent.current()?.name, model: item, variant: nextVariant })
          if (!models.visible(item)) models.setVisibility(item, true)
        })
      },
      visible(item: ModelKey) {
        return models.visible(item)
      },
      setVisibility(item: ModelKey, visible: boolean) {
        models.setVisibility(item, visible)
      },
      variant: {
        configured,
        selected,
        current() {
          return resolveModelVariant({
            variants: this.list(),
            selected: this.selected(),
            configured: this.configured(),
            catalog: current() !== undefined,
          })
        },
        list() {
          const item = current()
          // Only surface a persisted level the catalog lacks while the model is
          // still hydrating; a loaded catalog is authoritative.
          return listModelVariants(item?.variants, item ? undefined : this.selected())
        },
        set(value: string | undefined) {
          batch(() => {
            if (id()) userSelectedSessions.add(id()!)
            if (selected() === (value ?? null)) return
            const model = current()
            const key = model ? modelKey({ providerID: model.provider.id, modelID: model.id }) : undefined
            const variants = key ? { ...(scope()?.variants ?? {}), [key]: value ?? null } : scope()?.variants
            // Only this chat changes; new chats keep starting at the default.
            write({ model: selectedModel(), variant: value ?? null, variants, source: "user", messageID: scope()?.messageID })
            if (model) {
              persistSessionSelection({
                agent: agent.current()?.name,
                model: { providerID: model.provider.id, modelID: model.id },
                variant: value ?? null,
              })
            }
          })
        },
        cycle() {
          const items = this.list()
          if (items.length === 0) return
          this.set(
            cycleModelVariant({
              variants: items,
              selected: this.selected(),
              configured: this.configured(),
            }),
          )
        },
      },
      serviceTier: {
        list() {
          return current()?.serviceTiers ?? []
        },
        /** The account's tier for the model while it still offers it; undefined is standard speed. */
        current() {
          const item = current()
          if (!item) return undefined
          const value = models.serviceTier({ providerID: item.provider.id, modelID: item.id })
          return this.list().some((tier) => tier.id === value) ? value : undefined
        },
        /** Sets the tier every chat with the model uses, on every device. */
        set(value: string | undefined) {
          const item = current()
          if (!item) return
          void models.setServiceTier({ providerID: item.provider.id, modelID: item.id }, value)
        },
      },
    }

    const result = {
      model,
      agent,
      selection: {
        markUsed(input: { agent: string; model: ModelKey; variant?: string }) {
          const session = id()
          if (session) {
            userSelectedSessions.delete(session)
            setSubmittedSession(session)
          }
          setSaved("lastUsed", reconcile({
            agent: newChatAgent(input.agent),
            model: input.model,
            source: "message",
          }))
          models.recent.push({ ...input.model, variant: input.variant ?? null })
        },
      },
      session: {
        reset() {
          const seed = draftSeed()
          batch(() => setDraft(seed, false))
        },
        promote(_dir: string, session: string) {
          const next = cloneSelection(snapshot())
          if (!next) return
          batch(() => {
            setSaved("session", session, next)
            const draft = draftID()
            if (draft && saved.drafts) setSaved("drafts", draft, undefined)
            else setSaved("draft", undefined)
          })
        },
        discard(session: string) {
          setSaved("session", session, undefined)
        },
        restoreFromSession(s: { id: string; agent?: string; providerID?: string; modelID?: string; variant?: string }) {
          if (!savedReady()) return
          const session = id()
          if (!session || s.id !== session) return
          if (!s.providerID || !s.modelID) return
          if (userSelectedSessions.has(session)) return
          const current = saved.session[session]
          const next = sessionSelection(s, current)
          if (!next || sameSessionModelState(current, next)) return
          setSaved("session", session, reconcile(next))
        },
        restore(msg: { id: string; sessionID: string; agent: string; model: ModelKey; variant?: string }) {
          if (!savedReady()) return
          const session = id()
          if (!session) return
          if (msg.sessionID !== session) return
          const current = saved.session[session]
          if (!messageCanRestoreModel(current?.source, userSelectedSessions.has(session))) return

          const next = messageSelection(msg, current)
          if (sameSessionModelState(current, next)) return
          setSaved("session", session, reconcile(next))
        },
      },
    }

    createEffect(() => {
      if (!savedReady()) return
      const sessionID = id()
      if (!sessionID) return
      const hydration = sessionModelHydration(
        sessionID,
        globalSync.data.session,
        globalSync.data.message[sessionID] ?? [],
      )
      if (!hydration) return
      if (hydration.source === "session") {
        result.session.restoreFromSession(hydration)
        if (submittedSession() === sessionID) setSubmittedSession(undefined)
        return
      }
      result.session.restore(hydration.message)
    })

    if (modelEnabled()) {
      createEffect(() => {
        const agent = result.agent.current()
        const model = result.model.current()
        modelProbe.set({
          dir: activeDir(),
          sessionID: id(),
          agent: agent?.name,
          model: model
            ? {
                providerID: model.provider.id,
                modelID: model.id,
                name: model.name,
              }
            : undefined,
          variant: result.model.variant.current() ?? null,
          selected: result.model.variant.selected(),
          configured: result.model.variant.configured(),
          pick: scope(),
          base: undefined,
        })
      })

      onCleanup(() => modelProbe.clear())
    }

    return result
  },
})
